import { Navigate, useSearch } from '@tanstack/react-router';
import { Globe } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import productionDeclarationGuidelineDecisionTree from '@/assets/images/production-declaration-guideline-decision-tree.png';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@/components/ui/select';
import { ProductionDeclarationOrderBanner } from '@/features/production-declaration/components/shared/production-declaration-order-banner';
import { ProductionDeclarationScrollToTopButton } from '@/features/production-declaration/components/shared/production-declaration-scroll-to-top-button';
import {
  GUIDELINE_LANGUAGE_ORDER,
  getProductionDeclarationGuidelineHtml,
} from '@/features/production-declaration/hooks/guideline/get-production-declaration-guideline-html';
import { useProductionDeclarationGuidelineFlow } from '@/features/production-declaration/hooks/guideline/use-production-declaration-guideline-flow';
import { useGetProductionDeclaration } from '@/services/apis/queries/production-declaration/use-get-production-declaration';

type ProductionDeclarationGuidelineContentProps = {
  productionDeclarationId: string;
};

export function ProductionDeclarationGuidelineContent({
  productionDeclarationId,
}: ProductionDeclarationGuidelineContentProps) {
  const { t } = useTranslation('productionDeclaration');
  const { token } = useSearch({ strict: false });
  const { data } = useGetProductionDeclaration(productionDeclarationId, token);

  const supplierName =
    data.supplierName?.trim() || t('orderBanner.supplierFallbackName');
  const { redirectTo } = useProductionDeclarationGuidelineFlow({
    productionDeclarationId,
    token,
    statusCode: data.status.code,
  });
  const [selectedLanguage, setSelectedLanguage] = useState<string>(
    GUIDELINE_LANGUAGE_ORDER[0].code,
  );

  if (redirectTo) {
    return (
      <Navigate
        params={{ id: productionDeclarationId }}
        replace
        search={{ token }}
        to={redirectTo}
      />
    );
  }

  return (
    <div className='space-y-12'>
      <ProductionDeclarationOrderBanner
        actionLabel={t('guideline.cta')}
        actionTo='/production-declarations/$id/statement'
        productionDeclarationId={productionDeclarationId}
        purchaseOrderName={data.purchaseOrderName}
        supplierName={data.supplierName}
        token={token}
      />
      <div className='space-y-6'>
        <Select onValueChange={setSelectedLanguage} value={selectedLanguage}>
          <SelectTrigger className='h-10 rounded-lg border-declaration-border bg-white px-4 font-semibold text-declaration-primary shadow-none transition-colors hover:border-declaration-primary-border hover:bg-declaration-primary-light focus-visible:border-declaration-primary focus-visible:ring-2 focus-visible:ring-declaration-primary/20 dark:border-declaration-border dark:bg-white dark:text-declaration-primary dark:hover:bg-declaration-primary-light [&_svg:not([class*="text-"])]:text-declaration-primary'>
            <Globe className='size-4 text-declaration-primary' />
            <span className='leading-none'>
              {selectedLanguage.toUpperCase()}
            </span>
          </SelectTrigger>
          <SelectContent className='border-declaration-border bg-white text-declaration-foreground shadow-[0_10px_24px_rgba(17,24,39,0.08)] dark:border-declaration-border dark:bg-white dark:text-declaration-foreground'>
            {GUIDELINE_LANGUAGE_ORDER.map(({ code, label }) => (
              <SelectItem
                className='focus:bg-declaration-primary-light focus:text-declaration-foreground data-[state=checked]:text-declaration-primary'
                key={code}
                value={code}
              >
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div
          className='max-w-none text-declaration-foreground [&_a:hover]:text-declaration-link-hover [&_a]:text-declaration-link [&_a]:underline [&_code]:font-inherit [&_code]:text-declaration-foreground [&_h2]:mb-10 [&_h2]:font-semibold [&_h2]:text-4xl [&_h2]:text-declaration-foreground [&_h2]:leading-[1.12] [&_h2]:tracking-tight [&_h3]:mb-5 [&_h3]:pt-2 [&_h3]:font-semibold [&_h3]:text-2xl [&_h3]:text-declaration-foreground [&_h3]:leading-tight [&_li]:text-[1.05rem] [&_li]:leading-[1.42] [&_ol]:mb-8 [&_ol]:list-decimal [&_ol]:space-y-5 [&_ol]:pl-8 [&_p]:mb-6 [&_p]:text-[1.05rem] [&_p]:leading-[1.42] [&_strong]:font-semibold [&_strong]:text-declaration-foreground [&_ul]:mb-8 [&_ul]:list-disc [&_ul]:space-y-3 [&_ul]:pl-8'
          dangerouslySetInnerHTML={{
            __html: getProductionDeclarationGuidelineHtml(selectedLanguage, {
              supplierName,
            }),
          }}
        />
      </div>
      <figure className='overflow-hidden rounded-[1.75rem] border border-declaration-border bg-declaration-skeleton-warm p-3 shadow-[0_18px_40px_rgba(56,78,57,0.08)] sm:p-4'>
        <img
          alt='Supplier decision tree for plot-level traceability and documentation'
          className='h-auto w-full rounded-[1.25rem]'
          height={1012}
          loading='lazy'
          src={productionDeclarationGuidelineDecisionTree}
          width={1800}
        />
      </figure>
      <ProductionDeclarationScrollToTopButton />
    </div>
  );
}
